import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { apiClient } from '../client';
import { queryKeys } from '../react-query.config';
import { resolveTenantFromUrl } from '@low6-pickem/frontend-shared';

// Use resolved tenant slug (from URL or environment)
const DEFAULT_TENANT_SLUG = resolveTenantFromUrl();

export const usePickemLeaderboard = ({ pickemId }: { pickemId?: string }) =>
  useInfiniteQuery({
    queryKey: queryKeys.leaderboard.pickem(pickemId),
    queryFn: async ({ pageParam = 0 }) => {
      return apiClient.get('/api/leaderboard/pickem/{id}', {
        pathParams: { id: pickemId as string },
        params: {
          limit: pageParam === 0 ? 100 : 50,
          offset: pageParam,
        },
      });
    },
    getNextPageParam: lastPage => {
      // API enforces max offset of 500, so stop fetching at that limit
      const nextOffset = lastPage.offset + lastPage.limit;
      if (nextOffset >= 500) {
        return undefined; // Stop fetching at 500 entry limit
      }
      // Return the next offset if there's more data, otherwise undefined to stop fetching
      return lastPage.hasMore ? nextOffset : undefined;
    },
    initialPageParam: 0,
    enabled: !!pickemId,
    staleTime: 30000,
  });

// Removed unused hooks:
// - useTournamentLeaderboard
// - useGetLeaderboard
// - useGetPickemLeaderboardPosition
// - useGetUserLeaderboardPosition
// These were only used by the deleted useLeaderboard.ts hook

export interface AggregateLeaderboardEntry {
  user_id: string;
  username: string;
  total_points: number;
  rank: number;
  pickems_data?: string | null;
  prize?: {
    cash: number | null;
    crowns: number | null;
    other: string | null;
  } | null;
}

export const useAggregateLeaderboard = (
  period: 'today' | 'week' | 'month' | 'year',
  weekOffset: number = 0,
  leagueCode?: string
) =>
  useInfiniteQuery({
    queryKey: ['leaderboard', 'aggregate', period, weekOffset, leagueCode],
    queryFn: async ({ pageParam = 0 }) => {
      // Use current tenant slug as fallback if leagueCode is not provided
      const validLeagueCode = leagueCode || DEFAULT_TENANT_SLUG;

      return apiClient.get('/api/leaderboards/aggregate', {
        params: {
          period,
          limit: pageParam === 0 ? 100 : 50,
          offset: pageParam,
          week_offset: weekOffset,
          league_code: validLeagueCode,
        },
      });
    },
    getNextPageParam: lastPage => {
      // API enforces max offset of 500, so stop fetching at that limit
      const nextOffset = lastPage.offset + lastPage.limit;
      if (nextOffset >= 500) {
        return undefined; // Stop fetching at 500 entry limit
      }
      // Return the next offset if there's more data, otherwise undefined to stop fetching
      return lastPage.hasMore ? nextOffset : undefined;
    },
    initialPageParam: 0,
    staleTime: 30000,
  });

export interface UserPickemEntry {
  pickem_id: string;
  pickem_title: string;
  pickem_title_en?: string;
  pickem_title_fr?: string | null;
  points: number;
  rank: number | null;
  prize?: {
    cash: number | null;
    crowns: number | null;
    other: string | null;
  } | null;
}

export const useUserPickemsForPeriod = (
  userId: string | undefined,
  period: 'today' | 'week' | 'month' | 'year',
  weekOffset: number = 0,
  leagueCode?: string
) =>
  useQuery({
    queryKey: ['leaderboard', 'user-pickems', userId, period, weekOffset, leagueCode],
    queryFn: async () => {
      if (!userId) throw new Error('User ID is required');
      // Use current tenant slug as fallback if leagueCode is not provided
      const validLeagueCode = leagueCode || DEFAULT_TENANT_SLUG;
      return apiClient.get('/api/leaderboards/aggregate/user/{userId}/pickems', {
        pathParams: { userId: userId },
        params: { period, week_offset: weekOffset, league_code: validLeagueCode },
      });
    },
    enabled: !!userId,
    staleTime: 30000,
  });

// Hook for fetching user picks for a specific pickem
export interface UserPickemPick {
  question_id: string;
  question_text: string | null;
  question_text_en?: string | null;
  question_text_fr?: string | null;
  option_text: string | null;
  option_text_en?: string | null;
  option_text_fr?: string | null;
  option_image: string | null;
  is_correct: boolean | null;
  points_earned: number;
}

export const useUserPickemPicks = (pickemId: string | undefined, userId: string | undefined) =>
  useQuery({
    queryKey: ['leaderboard', 'pickem-picks', pickemId, userId],
    queryFn: async () => {
      if (!pickemId || !userId) throw new Error('Pickem ID and User ID are required');
      return apiClient.get('/api/leaderboard/pickem/{pickemId}/user/{userId}/picks', {
        pathParams: { pickemId, userId },
      });
    },
    enabled: !!pickemId && !!userId,
    staleTime: 30000,
  });

// Hook for fetching user's weekly breakdown (used in season view)
export interface WeeklyBreakdownEntry {
  week_number: number;
  week_label: string;
  rank: number | null;
  points: number;
  prize?: {
    cash: number | null;
    crowns: number | null;
    other: string | null;
  } | null;
}

export const useUserWeeklyBreakdown = (userId: string | undefined, leagueCode?: string) =>
  useQuery({
    queryKey: ['leaderboard', 'weekly-breakdown', userId, leagueCode],
    queryFn: async () => {
      if (!userId) throw new Error('User ID is required');
      // Use current tenant slug as fallback if leagueCode is not provided
      const validLeagueCode = leagueCode || DEFAULT_TENANT_SLUG;
      return apiClient.get('/api/leaderboards/aggregate/user/{userId}/weekly-breakdown', {
        pathParams: { userId },
        params: { league_code: validLeagueCode },
      });
    },
    enabled: !!userId,
    staleTime: 30000,
  });
